/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2018 Adobe
 *  All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/

/* eslint-disable no-unused-vars */
import * as React from 'react';
import {Route} from 'react-router-dom';
import Button from '@react/react-spectrum/Button';
import ButtonGroup from '@react/react-spectrum/ButtonGroup';
import AnalyticsUtil from '../../../utils/AnalyticsUtil';
import {FormattedMessage} from 'react-intl';

/* eslint-enable no-unused-vars */
import './LeftNav.scss';
import {matchRoutes} from 'react-router-config';
import {
	linksLearnEntitle,
	SEARCH_TAB,
	YOUR_WORK_TAB,
	DISCOVER_TAB,
	LIBRARIES_TAB,
	STOCK_MP_TAB,
} from './constants';
import {connect} from 'react-redux';
import {setLeftNavOpen} from '../../actions/appActions';
import {RecentAssetsAnalyticsUtil} from 'recent-assets';
import {getLocalizedURL} from '@intl/adobe-locales';
import getConfig from '../../../config';

type Props = {
	routes: string;
	leftNavOpen: boolean;
	forceHideMenu: boolean;
	forceHideNavbar: boolean;
	setLeftNavOpen: Function;
	pathName: string;
	planCode: string;
	productEntitlements: Array<string>;
	storageQuota: number;
	language: string;
	overwriteComponent: any;
};

type State = {
	currentValue: string;
	colapsableLeftNav: boolean;
	searching: boolean;
};

let links = linksLearnEntitle; // By default, we assume the user has entitlements for Learn playlists

export class LeftNav extends React.Component<Props, State> {
	constructor(props: Props) {
		super(props);
		this.state = {
			currentValue: 'button-0',
			colapsableLeftNav: false,
			searching: false,
		};
	}

	componentDidMount() {
		this.setCurrentValue(this.props.pathName);
		if (!AnalyticsUtil.showTopNav) {
			const analytics = new AnalyticsUtil();
			analytics.postMainAction('render', true);
		}

		const event = new CustomEvent('breakpointChanged', {detail: {name: window.cchome.util.currentBreakpoint}});
		this.onBreakpointChanged(event);

		window.addEventListener('breakpointChanged', this.onBreakpointChanged);
	}

	componentWillUnmount() {
		window.removeEventListener('breakpointChanged', this.onBreakpointChanged);
	}

	componentDidUpdate(prevProps: Props) {
		if (this.props.pathName !== prevProps.pathName) {
			this.setCurrentValue(this.props.pathName);
			if (!AnalyticsUtil.showTopNav) {
				const analytics = new AnalyticsUtil();
				analytics.postMainAction('render', false);
			}
		}

		// Bug CCH-2372: Left nav takes time to adjust to position when scrolled up and down (Android Tablet specific issue)
		// Fixed: disable scrolling when left nav is open
		const rootElement = document.getElementById('root');
		if (rootElement && window.cchome.util.isAndroid()) {
			if (this.props.leftNavOpen) {
				rootElement.classList.add('prevent-scrolling');
			}
			else {
				rootElement.classList.remove('prevent-scrolling');
			}
		}
	}

	updateLinks() {
		if (this.props.planCode && this.props.planCode.includes('k12')) {
			if (!window.cchome.util.hasFeature('enable_discover_k12')) {
				links = links.filter(item => item.tab !== DISCOVER_TAB);
			}

			if (this.props.storageQuota === 0) {
				links = links.filter(item => item.tab !== YOUR_WORK_TAB);
			}
		}

		// show stock and marketplace tab under ff
		if (window.cchome.util.hasFeature('topnav_stock')) {
			if (!links.some(value => value.title === 'app.navtab.stockmarketplace')) {
				links.push({
					title: 'app.navtab.stockmarketplace',
					tab: STOCK_MP_TAB,
					url: AnalyticsUtil.appendUrlWithAnalytics(getLocalizedURL(getConfig('STOCK_URL'), this.props.language)),
				});
			}
		}
	}

	onBreakpointChanged = (e: any) => {
		const breakpoint = e.detail.name;
		switch (breakpoint) {
			case 'L':
			case 'LM':
			case 'M':
			case 'MS':
				this.props.setLeftNavOpen(false);
				this.setState({colapsableLeftNav: false});
				break;
			default:
				this.setState({colapsableLeftNav: true});
				break;
		}
	};

	setCurrentValue(pathName: string) {
		let index = 0;

		// Initialize the AnalyticsUtil with
		// default values
		AnalyticsUtil.leftNavBarIndex = index;

		const matches = matchRoutes(this.props.routes, pathName);
		let tab = '';
		if (matches && matches.length > 0) {
			tab = matches[0].route.tab;
		}

		if (tab === SEARCH_TAB) {
			this.setState({
				searching: true,
			});
			return;
		}

		for (let i = 0; i < links.length; i++) {
			if (links[i].tab === tab) {
				index = i;
				break;
			}
		}

		this.setState({
			searching: false,
			currentValue: `button-${index}`,
		});

		// Reset the AnalyticsUtil with
		// current value
		AnalyticsUtil.leftNavBarIndex = index;

		RecentAssetsAnalyticsUtil.appName = getConfig('APP_NAME');
		RecentAssetsAnalyticsUtil.location = AnalyticsUtil.getLeftTab();
	}

	onChange(history: any, value: string) {
		const index = parseInt(value.slice(-1));
		const path = links[index].path ? links[index].path : null;
		const url = links[index].url ? links[index].url : null;
		const title = links[index].title ? links[index].title : null;

		AnalyticsUtil.leftNavBarIndex = index;

		const analytics = new AnalyticsUtil();
		analytics.postMainAction('click', null);

		if (path) {
			history.push(links[index].path);
		}
		else if (url) {
			window.open(getLocalizedURL(url, this.props.language), '_self');
		}
		this.closeMenu();
		window.cchome.navigationOccured = true;
	}

	closeMenu = () => {
		this.props.setLeftNavOpen(false);
	};

	render() {
		const hasTopBanner = !!document.getElementsByClassName('ccmob-top-banner-container')[0];
		let leftnavContainerClass = 'leftnav-container';

		if (this.props.forceHideMenu && !this.props.leftNavOpen) {
			leftnavContainerClass = 'leftnav-container-force-menu hidden';
		}
		if (this.props.leftNavOpen) {
			leftnavContainerClass += ' open';
		}
		if (this.state.searching) {
			leftnavContainerClass += ' searching';
		}
		if (hasTopBanner) {
			leftnavContainerClass += ' with-top-banner';
		}

		this.updateLinks();

		if (this.props.overwriteComponent && !this.state.colapsableLeftNav) {
			return (
				<div className='leftnav-wrapper'>
					<div
						className={
							this.props.leftNavOpen && this.state.colapsableLeftNav ? 'leftnav-overlay' : 'hidden'
						}
						onClick={this.closeMenu}
					>
						&nbsp;
					</div>
					<div role='navigation' className={leftnavContainerClass}>
						{this.props.overwriteComponent}
					</div>
				</div>
			);
		}

		return (
			<div className={this.props.forceHideNavbar || !this.state.colapsableLeftNav ? 'hidden' : 'leftnav-wrapper'}>
				<div
					className={this.props.leftNavOpen && this.state.colapsableLeftNav ? 'leftnav-overlay' : 'hidden'}
					onClick={this.closeMenu}
				>
					&nbsp;
				</div>
				<div
					aria-hidden={this.state.colapsableLeftNav && !this.props.leftNavOpen}
					role='navigation'
					className={leftnavContainerClass}
				>
					<Route
						render={({history}) => (
							<ButtonGroup
								value={this.state.currentValue}
								onChange={value => this.onChange(history, value)}
								orientation='vertical'
							>
								{links.map((item, index) => {
									if (item.tab === LIBRARIES_TAB) {
										return <></>;
									}

									const label = <FormattedMessage id={item.title} />;
									return (
										<Button key={index} value={`button-${index}`}>
											{label}
										</Button>
									);
								})}
							</ButtonGroup>
						)}
					/>
				</div>
			</div>
		);
	}
}

function mapStateToProps(state) {
	return {
		forceHideMenu: state.app.forceHideMenu,
		forceHideNavbar: state.app.forceHideNavbar,
		leftNavOpen: state.app.leftNavOpen,
		productEntitlements: state.profile.productEntitlements,
		planCode: state.profile.subscriptionInfo.planCode,
		storageQuota: state.profile.storage.storage_quota,
		language: state.app.language,
	};
}

export default connect(mapStateToProps, {setLeftNavOpen})(LeftNav);
